import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Loader2, RotateCw } from "lucide-react";
import GameShell from "../components/GameShell";
import ClaimFlow from "../components/ClaimFlow";
import AccountButton from "../components/AccountButton";
import { start as startRunner } from "../game/manicouRunner";
import { rewardToday, runnerFinish, adsActive, adImpression } from "../lib/api";
import { getDeviceId } from "../lib/device";

const LEVELS = [
  { key: "facile", label: "Facile", emoji: "🐢" },
  { key: "normal", label: "Normal", emoji: "🏃" },
  { key: "difficile", label: "Difficile", emoji: "🔥" },
  { key: "extreme", label: "Extrême", emoji: "💥" },
];

// Paliers d'une barre, triés par seuil croissant. `onlyBonus` : true = paliers bonus, false = gains du jour.
function sortPaliers(reward, onlyBonus) {
  const rw = (reward?.rewards || []).filter((r) => (r.threshold ?? 0) > 0 && (onlyBonus ? !!r.bonus : !r.bonus));
  return [...rw].sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
}
// Gain du jour affiché = le lot NON bonus au seuil le plus bas.
function pickTarget(reward) {
  const ps = sortPaliers(reward, false);
  return ps[0] || null;
}
// Valeur d'une gourmandise (points bonus) selon la difficulté.
function itemValueFor(reward, difficulty) {
  const v = reward?.difficulty?.[difficulty]?.item_value;
  return Number.isFinite(v) && v > 0 ? v : 10;
}

// Jauge à paliers : un compteur remplit la barre ; chaque lot est un palier à débloquer.
function Gauge({ paliers, points, mystery, caption, unit = "pts" }) {
  if (!paliers.length) return null;
  const top = Math.max(1, paliers[paliers.length - 1].threshold || 1);
  const pct = Math.min(100, (points / top) * 100);
  const next = paliers.find((r) => points < (r.threshold || 0)); // prochain palier à atteindre
  const cur = next || paliers[paliers.length - 1];
  const remaining = Math.max(0, (cur.threshold || 0) - points);
  const allDone = !next;
  const hideName = mystery && !cur.bonus;               // gain du jour mystère : caché ; paliers bonus : visibles
  const label = hideName ? "Récompense mystère" : cur.name;
  const img = hideName ? null : cur.image;
  return (
    <div className="w-full max-w-md mx-auto bg-black/45 backdrop-blur rounded-2xl px-3 py-2 text-white pointer-events-none">
      {caption && <p className="text-[10px] font-bold uppercase tracking-wide text-white/60 mb-1">{caption}</p>}
      <div className="flex items-center gap-3">
        {img ? (
          <img src={img} alt={label} className="flex-none w-10 h-10 rounded-xl object-cover bg-white/10" />
        ) : (
          <div className="flex-none w-10 h-10 rounded-xl bg-white/15 grid place-items-center text-xl">{cur.bonus ? "🏆" : "🎁"}</div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight truncate">
            {cur.bonus && <span className="text-[#F9B81E]">★ </span>}{label}
          </p>
          <p className="text-[11px] text-white/80 leading-tight">
            {allDone ? "🎉 Tous les lots débloqués !" : `Encore ${remaining} ${unit}`}
          </p>
        </div>
        <div className="flex-none text-right">
          <span className="font-black text-lg leading-none">{points}</span>
          <span className="text-white/70 text-xs">/{top}</span>
        </div>
      </div>
      <div className="mt-2.5 relative h-2.5 w-full rounded-full bg-white/20">
        <div className="absolute inset-0 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-200"
            style={{ width: `${pct}%`, background: allDone ? "#7CFC5A" : "linear-gradient(90deg,#F9B81E,#E20613)" }} />
        </div>
        {paliers.map((r, i) => {
          const at = Math.min(100, ((r.threshold || 0) / top) * 100);
          const hit = points >= (r.threshold || 0);
          return (
            <span key={i} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{ left: `${at}%` }}>
              <span className={`block w-2.5 h-2.5 rounded-full border-2 ${hit ? "bg-[#7CFC5A] border-white" : r.bonus ? "bg-[#F9B81E] border-white/80" : "bg-white/80 border-white/50"}`} />
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function RunnerPage() {
  const [phase, setPhase] = useState("select"); // select | play | result
  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [difficulty, setDifficulty] = useState("normal");
  const [points, setPoints] = useState(0);       // compteur SCORE (course/distance) → gain du jour
  const [bonusPoints, setBonusPoints] = useState(0); // compteur BONUS (gourmandises) → paliers bonus
  const [finish, setFinish] = useState(null);    // réponse /runner/finish
  const [claimQueue, setClaimQueue] = useState([]); // lots à réclamer (gain du jour puis bonus)
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== "undefined" ? window.matchMedia("(orientation: portrait)").matches : false
  );

  const canvasRef = useRef(null);
  const controllerRef = useRef(null);
  const adsRef = useRef(null);
  const deviceId = getDeviceId();

  const target = useMemo(() => pickTarget(reward), [reward]);
  const mainPaliers = useMemo(() => sortPaliers(reward, false), [reward]);  // gains du jour (par score)
  const bonusPaliers = useMemo(() => sortPaliers(reward, true), [reward]);  // paliers bonus (par collecte)
  const itemValue = useMemo(() => itemValueFor(reward, difficulty), [reward, difficulty]);
  const mystery = reward?.mode === "mystery";

  // Niveaux réellement configurés (sinon on garde la liste complète).
  const levels = useMemo(() => {
    const cfg = reward?.difficulty || {};
    const present = LEVELS.filter((l) => cfg[l.key]);
    return present.length ? present : LEVELS;
  }, [reward]);

  const loadReward = useCallback(async (diff) => {
    setLoading(true);
    try {
      const data = await rewardToday(diff);
      setReward(data);
    } catch (e) {
      toast.error("Impossible de charger l'objectif du jour");
    } finally {
      setLoading(false);
    }
  }, []);

  // Objectif + pub au chargement.
  useEffect(() => {
    loadReward(difficulty);
    adsActive("runner").then((d) => { adsRef.current = d?.slots || []; }).catch(() => { adsRef.current = []; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suit l'orientation de l'écran (pour le badge « tourne ton écran »).
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const on = () => setIsPortrait(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  // Si un seul niveau est proposé, on l'impose.
  useEffect(() => {
    if (levels.length === 1 && difficulty !== levels[0].key) setDifficulty(levels[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels]);

  const handleDie = useCallback(async ({ score, collected: items, duration_s, impressions }) => {
    // Impressions pub (best-effort, ne bloque jamais).
    (impressions || []).forEach((imp) => adImpression({ ...imp, scene: "runner", device: deviceId, game: "runner" }));
    // Deux canaux : score (course) → gain du jour ; gourmandises → paliers bonus (calculé serveur).
    try {
      const res = await runnerFinish({
        device_id: deviceId,
        difficulty,
        score,
        duration_s,
        items_collected: items,
        target_reward_id: target?.reward_id || null,
      });
      setFinish(res);
      const queue = [];
      if (res.won && res.spin_id) queue.push({ is_win: true, label: res.reward?.name || "ton cadeau", spin_id: res.spin_id, kind: "Gain du jour" });
      if (res.bonus_won && res.bonus_spin_id) queue.push({ is_win: true, label: res.bonus_reward?.name || "ton bonus", spin_id: res.bonus_spin_id, kind: "Bonus 🏆" });
      setClaimQueue(queue);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur à l'enregistrement de la partie";
      setFinish({ won: false, error: msg, score });
      toast.error(msg);
    }
    setPhase("result");
  }, [deviceId, difficulty, target]);

  // Démarre / arrête le moteur quand on entre en jeu.
  useEffect(() => {
    if (phase !== "play" || !canvasRef.current) return;
    setPoints(0); setBonusPoints(0);
    let lastScore = -1, lastBonus = -1;
    // Deux compteurs en parallèle : score = course (distance) ; bonus = gourmandises × valeur.
    // La partie se termine à la mort du manicou ; on gagne sur chaque canal atteint.
    const ctrl = startRunner(canvasRef.current, {
      difficulty,
      ads: adsRef.current || [],
      onScore: ({ score, collected }) => {
        const bp = Math.round((collected || 0) * itemValue);
        // Injection dans les jauges par pas pour limiter les rendus (défilement fluide).
        if (score - lastScore >= 2 || score < lastScore) { lastScore = score; setPoints(score); }
        if (bp !== lastBonus) { lastBonus = bp; setBonusPoints(bp); }
      },
      onDie: handleDie,
    });
    controllerRef.current = ctrl;
    return () => { ctrl.stop(); controllerRef.current = null; };
  }, [phase, difficulty, handleDie, itemValue]);

  const playAgain = () => {
    setFinish(null);
    setClaimQueue([]);
    setPhase("play");
  };

  // ---------- Écran de sélection ----------
  if (phase === "select") {
    return (
      <GameShell title="Manicou Runner" subtitle="Cours, saute, ramasse !">
        {loading ? (
          <Loader2 className="animate-spin text-white mt-6" size={44} />
        ) : (
          <div className="w-full max-w-sm flex flex-col items-center gap-5 mt-2">
            {target && (
              <div className="bg-white text-ink rounded-3xl p-4 w-full text-center" style={{ boxShadow: "0 8px 0 #a81a0c" }}>
                <p className="text-xs font-bold text-ink/50 uppercase tracking-wide">Le gain du jour</p>
                <div className="flex items-center justify-center gap-3 mt-2">
                  {mystery || !target.image ? (
                    <div className="w-14 h-14 rounded-2xl bg-cream grid place-items-center text-3xl">🎁</div>
                  ) : (
                    <img src={target.image} alt={target.name} className="w-14 h-14 rounded-2xl object-cover" />
                  )}
                  <div className="text-left">
                    <p className="font-display font-bold text-lg leading-tight">{mystery ? "Récompense mystère" : target.name}</p>
                    <p className="text-sm text-ink/60">Atteins {target.threshold} points</p>
                  </div>
                </div>
              </div>
            )}

            <div className="w-full">
              <p className="text-white/90 font-bold text-sm mb-2 text-center">Choisis ta difficulté</p>
              <div className="grid grid-cols-2 gap-3">
                {levels.map((l) => (
                  <button
                    key={l.key}
                    data-testid={`diff-${l.key}`}
                    onClick={() => { setDifficulty(l.key); loadReward(l.key); }}
                    className={`rounded-2xl px-3 py-3 font-display font-bold text-base transition-transform ${
                      difficulty === l.key ? "bg-sun text-ink scale-[1.02]" : "bg-white/15 text-white hover:bg-white/25"
                    }`}
                    style={difficulty === l.key ? { boxShadow: "0 5px 0 #caa600" } : {}}
                  >
                    <span className="mr-1">{l.emoji}</span> {l.label}
                  </button>
                ))}
              </div>
              {levels.length > 1 && (
                <button
                  onClick={() => loadReward(difficulty)}
                  className="text-white/70 text-xs mt-2 underline w-full text-center"
                >
                  Recharger l'objectif pour ce niveau
                </button>
              )}
            </div>

            <button data-testid="runner-play" onClick={() => setPhase("play")} className="yum-cta mt-1">
              Jouer 🏃
            </button>
            <p className="text-white/80 text-xs text-center max-w-xs">
              Utilise les boutons pour sauter et te baisser. Ramasse les gourmandises pour remplir la jauge bonus !
            </p>
          </div>
        )}
      </GameShell>
    );
  }

  // ---------- Jeu plein écran + jauge ----------
  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden" style={{ touchAction: "none" }}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" />

      {/* Barre du haut */}
      <div className="absolute top-0 left-0 right-0 z-20 p-3 flex items-start gap-2 pointer-events-none">
        <Link
          to="/"
          onClick={() => controllerRef.current?.stop()}
          className="pointer-events-auto flex items-center gap-1 text-ink text-xs font-bold bg-white px-3 py-1.5 rounded-full shadow"
        >
          <ArrowLeft size={14} /> Quitter
        </Link>
        <div className="flex-1 flex flex-col gap-1.5">
          {phase === "play" && mainPaliers.length > 0 && (
            <Gauge paliers={mainPaliers} points={points} mystery={mystery} caption="🏃 Gain du jour — course" unit="pts" />
          )}
          {phase === "play" && bonusPaliers.length > 0 && (
            <Gauge paliers={bonusPaliers} points={bonusPoints} caption="🍬 Bonus — gourmandises" unit="pts bonus" />
          )}
        </div>
        <div className="pointer-events-auto"><AccountButton /></div>
      </div>

      {/* Boutons tactiles : gauche = se baisser (maintenir), droite = sauter.
          En paysage ils tombent sur les côtés du jeu, en portrait dans la zone noire sous le jeu. */}
      {phase === "play" && (
        <div className="absolute inset-x-0 bottom-0 z-20 flex items-end justify-between px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pointer-events-none select-none">
          <button
            aria-label="Se baisser"
            style={{ touchAction: "none" }}
            className="pointer-events-auto w-[4.5rem] h-[4.5rem] rounded-full bg-white/85 text-ink shadow-xl active:scale-90 active:bg-white transition-transform flex flex-col items-center justify-center font-black"
            onPointerDown={(e) => { e.preventDefault(); controllerRef.current?.duckStart(); }}
            onPointerUp={(e) => { e.preventDefault(); controllerRef.current?.duckEnd(); }}
            onPointerLeave={() => controllerRef.current?.duckEnd()}
            onPointerCancel={() => controllerRef.current?.duckEnd()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <ArrowDown size={28} strokeWidth={3} />
            <span className="text-[10px] leading-none mt-0.5">Baisser</span>
          </button>
          <button
            aria-label="Sauter"
            style={{ touchAction: "none" }}
            className="pointer-events-auto w-[4.5rem] h-[4.5rem] rounded-full bg-[#F9B81E] text-ink shadow-xl active:scale-90 transition-transform flex flex-col items-center justify-center font-black"
            onPointerDown={(e) => { e.preventDefault(); controllerRef.current?.jump(); }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <ArrowUp size={28} strokeWidth={3} />
            <span className="text-[10px] leading-none mt-0.5">Sauter</span>
          </button>
        </div>
      )}

      {/* Badge : invite à tourner l'écran (portrait uniquement) — le jeu est meilleur en paysage */}
      {phase === "play" && isPortrait && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-32 z-20 pointer-events-none">
          <div className="flex items-center gap-2 bg-black/75 text-white text-xs font-bold px-3.5 py-2 rounded-full shadow-lg">
            <RotateCw size={15} className="text-[#F9B81E] animate-spin" style={{ animationDuration: "2.4s" }} />
            Tourne ton écran pour mieux jouer
          </div>
        </div>
      )}

      {/* Résultat de fin de partie */}
      {phase === "result" && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 px-4" data-testid="runner-result">
          {claimQueue.length > 0 ? (
            <ClaimFlow key={claimQueue[0].spin_id} result={claimQueue[0]} onClose={() => setClaimQueue((q) => q.slice(1))} />
          ) : (finish?.won || finish?.bonus_won) ? (
            <div className="bg-white text-ink rounded-3xl p-7 max-w-sm w-full text-center shadow-2xl">
              <h2 className="text-2xl font-black text-gray-800 mb-2">Bien joué ! 🎉</h2>
              <p className="text-gray-500 mb-4">Tes cadeaux sont dans « Mon compte ». À toi de rejouer !</p>
              <div className="flex gap-2">
                <button onClick={playAgain} className="flex-1 bg-[#E20613] text-white font-black py-3 rounded-2xl hover:bg-[#c40510]">Rejouer 🏃</button>
                <Link to="/" className="flex-1 bg-gray-200 font-bold py-3 rounded-2xl grid place-items-center">Accueil</Link>
              </div>
            </div>
          ) : (
            <div className="bg-white text-ink rounded-3xl p-7 max-w-sm w-full text-center shadow-2xl">
              <h2 className="text-2xl font-black text-gray-800 mb-1">Presque ! 😅</h2>
              <div className="text-gray-500 mb-4 space-y-1 text-sm">
                {target && (
                  <p>🏃 Gain du jour : il te fallait <b>{finish?.threshold ?? target.threshold} pts</b>
                    {mystery ? "" : ` (${target.name})`}. Tu as fait <b>{finish?.score ?? 0}</b>.</p>
                )}
                {bonusPaliers.length > 0 && finish?.bonus_threshold != null && (
                  <p>🍬 Bonus : <b>{finish.bonus_threshold} pts bonus</b> à ramasser. Tu as fait <b>{finish?.bonus_points ?? 0}</b>.</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={playAgain} className="flex-1 bg-[#E20613] text-white font-black py-3 rounded-2xl hover:bg-[#c40510]">
                  Rejouer 🏃
                </button>
                <Link to="/" className="flex-1 bg-gray-200 font-bold py-3 rounded-2xl grid place-items-center">Accueil</Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
